import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { TireFormData, TiresFormService } from '../../services/tires-form.service';
import { take, takeUntil } from 'rxjs/operators';
import { get, isEqual as _isEqual } from 'lodash';
import * as Values from './tire-description-form.values';
import {
  commentsAndCostRequiredByTreadValidator,
  commentsRequiredIfCostValidator,
  isValidCost,
  isValidValue,
  requiredWhenNotSpare
} from './tire-description-form.validators';
import { CustomValidatorConfig, PhotoUploadService } from '../../services/photo-upload.service';
import { InspectionImageDto, LaunchDarklyService } from 'remarketing-angular-library';
import { ExistingPhotosModalComponent } from '../existing-photos-modal/existing-photos-modal.component';
import { tireLocationCodes } from '../../data/photos-location-list';
import {PostingAnalyticsHelperService} from '../../services/posting-analytics-helper.service';
import { TranslocoService } from '@jsverse/transloco';
import { EditPostingUtilsService } from '../../services/edit-posting-utils.service';
import { SaComponent } from 'smartauction-frontend-micro-library';
import { CommonModule } from '@angular/common';
import { PhotosCardComponent } from '../photos-card/photos-card.component';
import { NgxMaskModule } from 'ngx-mask';

@Component({
    selector: 'app-tire-description-form',
    templateUrl: './tire-description-form.component.html',
    styleUrls: ['./tire-description-form.component.scss'],
    providers: [PhotoUploadService],
    imports: [ReactiveFormsModule, CommonModule, PhotosCardComponent, ExistingPhotosModalComponent, NgxMaskModule]
})
export class TireDescriptionFormComponent extends SaComponent implements OnInit, OnDestroy {
  @ViewChild(ExistingPhotosModalComponent) existingPhotosModal: ExistingPhotosModalComponent;
  manufacturers = Object.assign([], Values.MANUFACTURERS);
  tiresSizes = Object.assign([], Values.TIRE_SIZES);
  treadDepths = Object.assign([], Values.TREAD_DEPTHS);
  private _tire: Partial<TireFormData> = {};
  currTirelocation: string;
  uploadedImgList: InspectionImageDto[];
  existingPhotoList: InspectionImageDto[];
  textComplete: any;
  textErrorCount: any;
  @Input() set tire(tire: Partial<TireFormData>) {
    // the tire property is updated often, so this makes sure the form is stable.
    if (!_isEqual(tire, this._tire)) {
      this._tire = tire;
      this.resetForm(tire);
    }
  }
  get tire() {
    return this._tire;
  }
  @Input() currentVehicleVinNumber: string;
  @Output() update = new EventEmitter<TireFormData & { markTheSame: boolean }>();
  @Output() cancel = new EventEmitter();
  errorCount: '';
  errorFields: '';
  errMsg = '';
  form = this.formBuilder.group(
    {
      location: [''],
      manufacturer: ['', Validators.compose([requiredWhenNotSpare, isValidValue(this.manufacturers)])],
      size: ['', Validators.compose([requiredWhenNotSpare, isValidValue(this.tiresSizes)])],
      tread: ['', Validators.compose([requiredWhenNotSpare, isValidValue(this.treadDepths)])],
      comments: ['', Validators.maxLength(50)],
      damageCost: ['', isValidCost],
      markTheSame: [false]
    },
    { validators: [commentsAndCostRequiredByTreadValidator, commentsRequiredIfCostValidator] }
  );
  defaultPhotoCount = 5;
  tireFormCustomValidator: CustomValidatorConfig = {
    validatorName: 'validImageCount',
    validatorValue: 5,
    validatorErrMsgName: 'PHOTO_COUNT_ERROR',
    validatorErrMsgValue: "You can't upload more than five images."
  };

  tireDescriptionFormTranslations = {
    textErrorCount: '',
    textComplete: '',
    textManufacturer: '',
    textSelect: '',
    textEnterManufacturerContinue: '',
    textTireSize: '',
    textEnterSize: '',
    textTreadDepth: '',
    textEnterTreadDepth: '',
    labeMarkAllTire: '',
    textProvideTireDamage: '',
    ariaLabelTireDamageDescription: '',
    textEnterDamageDes: '',
    labelCost: '',
    textEnterCostContinue: '',
    labelTirePhoto: '',
    textSelect5: '',
    btnUploadNew: '',
    btnSelectExisting: '',
    btnAddChanges: '',
    btnCancel: ''
  };

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly tireFormService: TiresFormService,
    public readonly photoUploadService: PhotoUploadService,
    private readonly changeDetector: ChangeDetectorRef,
    public readonly editPostingUtilsService: EditPostingUtilsService,
    private readonly postingAnalyticsHelperService: PostingAnalyticsHelperService,
    private readonly translocoService: TranslocoService,
    private readonly launchDarklyService: LaunchDarklyService
  ) {
    super();
  }

  get isCanadianTenantEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }
  get isFrenchCanadian(): boolean {
    const frenchLanguage = this.translocoService.getActiveLang().includes('fr');
    return frenchLanguage && this.isCanadianTenantEnabled;
  }

  get groupedFormErrors(): { count?: number; errors?: string[] } {
    const {
      hasManufactureErrors,
      hasSizeErrors,
      hasTreadErrors,
      hasCommentsErrors,
      hasGlobalCommentsErrors,
      hasDamageCostErrors,
      hasGlobalCostError
    } = this.tireFormErrors();
    const count = [
      hasManufactureErrors,
      hasSizeErrors,
      hasTreadErrors,
      hasCommentsErrors || hasGlobalCommentsErrors,
      hasDamageCostErrors || hasGlobalCostError
    ].filter(Boolean).length;

    const manufactureErrorText = hasManufactureErrors && 'tire manufacturer';
    const sizeErrorText = hasSizeErrors && 'tire size';
    const treadErrorText = hasTreadErrors && 'tread depth';
    const commentErrorText = (hasCommentsErrors || hasGlobalCommentsErrors) && 'tire damage description';
    const damageCostErrorText = (hasDamageCostErrors || hasGlobalCostError) && 'cost';
    const errors = [manufactureErrorText, sizeErrorText, treadErrorText, commentErrorText, damageCostErrorText].filter(
      e => !!e
    );
    return { count, errors };
  }

  private tireFormErrors() {
    const hasManufactureErrors = Object.values(
      (get(this.manufacturer, 'dirty', false) && this.manufacturer.errors) || {}
    ).some(Boolean);
    const hasSizeErrors = Object.values((get(this.size, 'dirty', false) && this.size.errors) || {}).some(Boolean);
    const hasTreadErrors = Object.values((get(this.tread, 'dirty', false) && this.tread.errors) || {}).some(Boolean);
    const hasCommentsErrors = Object.values((get(this.comments, 'dirty', false) && this.comments.errors) || {}).some(
      Boolean
    );
    const hasDamageCostErrors = Object.values(
      (get(this.damageCost, 'dirty', false) && this.damageCost.errors) || {}
    ).some(Boolean);
    const hasGlobalCommentsErrors = this.form.getError('commentsRequired');
    const hasGlobalCostError = this.form.getError('costRequired');
    return {
      hasManufactureErrors,
      hasSizeErrors,
      hasTreadErrors,
      hasCommentsErrors,
      hasGlobalCommentsErrors,
      hasDamageCostErrors,
      hasGlobalCostError
    };
  }

  get multiErrorCount() {
    return this.groupedFormErrors.count;
  }

  get multiErrors() {
    if (this.multiErrorCount <= 1) {
      return undefined;
    }
    return this.groupedFormErrors.errors;
  }

  get manufacturer() {
    return this.form.get('manufacturer');
  }

  get size() {
    return this.form.get('size');
  }

  get tread() {
    return this.form.get('tread');
  }

  get comments() {
    return this.form.get('comments');
  }

  get damageCost() {
    return this.form.get('damageCost');
  }

  ngOnInit(): void {
    this.getTranslations();
    if (this.tiresSizes.filter(t => t === this.tire.size).length === 0) {
      this.tiresSizes.push(this.tire.size);
    }
    if (this.treadDepths.filter(t => t === this.tire.tread).length === 0) {
      this.treadDepths.push(this.tire.tread);
    }
    if (this.manufacturers.filter(t => t === this.tire.manufacturer).length === 0) {
      this.manufacturers.push(this.tire.manufacturer);
    }
    if (this.tireFormService.getTransientState(this.tire.location)) {
      this.resetForm(this.tireFormService.getTransientState(this.tire.location));
    } else {
      this.resetForm(this.tire);
    }
    this.form.valueChanges.pipe(takeUntil(super.destroyed$)).subscribe(values => {
      this.tireFormService.updateTransientState({
        ...this.tire,
        ...values
      });
    });
    this.constructPhotoCardList();
    this.updatesFromPhotoService();
  }

  ngOnDestroy(): void {
    super.destroyed$.next();
    super.destroyed$.complete();
  }

  formatCurrency() {
    const control = this.form.get('damageCost');
    if (!control?.value) {
      return;
    }
    const cost = Number(control.value);
    if (Number.isNaN(cost)) {
      this.form.patchValue({ damageCost: '' });
    } else {
      const damageCost = Number(cost.toString()).toFixed(2);
      this.form.patchValue({ damageCost });
    }
  }

  filterCommentValues() {
    const control = this.form.get('comments');
    if (!control?.value) {
      return;
    }
    this.form.patchValue({ comments: control.value.replace(/~/g, '') });
  }

  onSubmit() {
    if (this.form.status === 'INVALID') {
      this.manufacturer.markAsDirty();
      this.size.markAsDirty();
      this.tread.markAsDirty();
      this.comments.markAsDirty();
      this.damageCost.markAsDirty();
      return;
    }
    this.tireFormService.clearTransientState(this.tire.location);
    this.update.emit(this.form.getRawValue());
  }

  onCancel() {
    this.tireFormService.clearTransientState(this.tire.location);
    this.cancel.emit();
  }

  private resetForm(data: Partial<TireFormData>) {
    this.form.patchValue({
      location: data.location,
      manufacturer: data.manufacturer,
      size: data.size,
      tread: data.tread,
      comments: data.comments,
      damageCost: !data.damageCost || Number.isNaN(Number(data.damageCost)) ? '' : Number(data.damageCost).toFixed(2),
      markTheSame: false
    });
  }

  //Constructs the array for photo card comp
  constructPhotoCardList() {
    this.uploadedImgList = [];
    if (this.tire && this.tire.tireImages && this.tire.tireImages.length) {
      const tireImageList = [];
      this.tire.tireImages.forEach((item, index) => {
        if (index <= this.defaultPhotoCount - 1) {
          tireImageList.push({
            url: item.url,
            location: this.tire.location
          });
        }
      });
      this.uploadedImgList = tireImageList;
    }
    this.currTirelocation = this.tire.location;
    this.getAllInspectionPhotos();
  }

  getAllInspectionPhotos() {
    const existingInspectionImgList = this.editPostingUtilsService.constructExistingPhotosList();
    existingInspectionImgList.forEach((inspecItem: any) => {
      const inspectionUrl = inspecItem.url || inspecItem.sourceUrl;
      this.uploadedImgList.forEach(tireImg => {
        const tireUrl = tireImg.url || tireImg.sourceUrl;
        if (inspectionUrl === tireUrl) {
          inspecItem.isExistingImage = true;
        }
      });
    });
    this.existingPhotoList = existingInspectionImgList;
    this.changeDetector.detectChanges();
  }

  /**
   *
   * @param actionName - action from action bar component
   * its deletes the selected card
   */
  actionBarUpdatesFromPhotosCard(data) {
    const actionName = data.actionName;
    if (actionName === 'delete') {
      this.uploadedImgList = this.uploadedImgList.filter((item: any) => {
        return !item.isSelected && !item.showSpinner;
      });
      this.updateTiresPhotosToPosting();
      this.getAllInspectionPhotos();
    } else {
      this.uploadedImgList.forEach((item: any) => {
        delete item.isSelected;
      });
    }
  }

  //Subscribe subjects from photo upload service
  updatesFromPhotoService() {
    //gets VIW response from photo upload service
    this.photoUploadService.photoUploadResponseData.pipe(takeUntil(super.destroyed$)).subscribe((data: any) => {
      const response = data && data.response;
      if (response && response.showSpinner) {
        this.uploadedImgList.push(...response.spinnerList);
      } else {
        this.addResponseToPhotoList(response);
      }
    });

    //gets error status from photo upload service
    this.photoUploadService.photoUploadErrStatus.pipe(takeUntil(super.destroyed$)).subscribe((data: any) => {
      this.errMsg = data && data.errMsg;
      if (this.errMsg) {
        this.uploadedImgList = this.uploadedImgList.filter((item: any) => {
          return !item.showSpinner;
        });
      }
      this.changeDetector.markForCheck();
    });
  }

  //updates the photo card array with incoming VIW response
  addResponseToPhotoList(imageDetails) {
    if (this.uploadedImgList.length <= this.defaultPhotoCount) {
      const findSpinnerIndex = this.uploadedImgList.findIndex((imgItem: any) => {
        return imgItem.showSpinner;
      });
      const imgObj = {
        sourceUrl: imageDetails.files[0].fullSizedUrl,
        location: this.currTirelocation
      };
      this.uploadedImgList.splice(findSpinnerIndex, 1, imgObj);
      this.updateTiresPhotosToPosting();
      this.getAllInspectionPhotos();
    }
  }

  /**
   *
   * @param photoList - Image list from existing photo modal Component
   * Updates the latest image in the UI and save the date in store
   */
  photosFromExistingModal(modalUpdates) {
    const photoList = modalUpdates.photoList;
    const selectedImages = [];
    photoList.forEach((item: any) => {
      if (item.isExistingImage) {
        //if image is added from existing modal changing its location to current tire location
        item.location = this.currTirelocation;
        const { isExistingImage, ...rest } = item;
        selectedImages.push(rest);
      } else {
        //Needs to change the location if tire image is removed using existing modal
        item.location = item.location === this.currTirelocation ? '' : item.location;
      }
      delete item.isExistingImage;
    });
    this.uploadedImgList = selectedImages;
    this.updateTiresPhotosToPosting();
    this.editPostingUtilsService.updateInspectionImageData(photoList, true);
    this.getAllInspectionPhotos();
    tireLocationCodes.forEach(locationCode => {
      if (locationCode !== this.currTirelocation) {
        this.tireFormService.closeDrawer(locationCode);
      }
    });
  }

  //save the latest added/deleted photo card in posting state
  updateTiresPhotosToPosting() {
    this._tire.tireImages = this.uploadedImgList.map(item => {
      return { url: item.url || item.sourceUrl };
    }) as any;
    this.tireFormService.updateTire(this._tire as TireFormData);
  }

  selectPhotos() {
    const currentListLength = (this.uploadedImgList && this.uploadedImgList.length) || 0;
    this.tireFormCustomValidator.validatorValue = this.defaultPhotoCount - currentListLength;
    this.photoUploadService.selectPhotos(this.currentVehicleVinNumber, this.tireFormCustomValidator);
  }

  showExistingModal() {
    this.existingPhotosModal.show(0);
    this.postingAnalyticsHelperService.trackModalView('existingPhotos');
  }

  getTranslations() {
    const keyParams = {
      'sellModule.posting.tireDescriptionForm': {},
      common: {}
    };

    this.translocoService
      .selectTranslateObject({
        'sellModule.posting.tireDescriptionForm.textErrorCount': { multiErrorCount: this.multiErrorCount }
      })
      .pipe(take(1))
      .subscribe(translation => {
        this.tireDescriptionFormTranslations.textErrorCount = translation[0];
      });

    this.translocoService
      .selectTranslateObject({
        'sellModule.posting.tireDescriptionForm.textComplete': { multiErrors: this.multiErrors }
      })
      .pipe(take(1))
      .subscribe(translation => {
        this.tireDescriptionFormTranslations.textComplete = translation[0];
      });

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(super.destroyed$))
      .subscribe(translate => {
        this.tireDescriptionFormTranslations.textManufacturer = translate[0].textManufacturer;
        this.tireDescriptionFormTranslations.textSelect = translate[0].textSelect;
        this.tireDescriptionFormTranslations.textEnterManufacturerContinue = translate[0].textEnterManufacturerContinue;
        this.tireDescriptionFormTranslations.textTireSize = translate[0].textTireSize;
        this.tireDescriptionFormTranslations.textEnterSize = translate[0].textEnterSize;
        this.tireDescriptionFormTranslations.textTreadDepth = translate[0].textTreadDepth;
        this.tireDescriptionFormTranslations.textEnterTreadDepth = translate[0].textEnterTreadDepth;
        this.tireDescriptionFormTranslations.labeMarkAllTire = translate[0].labeMarkAllTire;
        this.tireDescriptionFormTranslations.ariaLabelTireDamageDescription =
          translate[0].ariaLabelTireDamageDescription;
        this.tireDescriptionFormTranslations.textEnterDamageDes = translate[0].textEnterDamageDes;
        this.tireDescriptionFormTranslations.labelCost = translate[0].labelCost;
        this.tireDescriptionFormTranslations.textEnterCostContinue = translate[0].textEnterCostContinue;
        this.tireDescriptionFormTranslations.labelTirePhoto = translate[0].labelTirePhoto;
        this.tireDescriptionFormTranslations.textSelect5 = translate[0].textSelect5;
        this.tireDescriptionFormTranslations.btnUploadNew = translate[0].btnUploadNew;
        this.tireDescriptionFormTranslations.btnSelectExisting = translate[0].btnSelectExisting;
        this.tireDescriptionFormTranslations.btnAddChanges = translate[0].btnAddChanges;
        this.tireDescriptionFormTranslations.btnCancel = translate[1].btnCancel;
      });
  }
}
